import cookies from 'js-cookie';

// Module scoped so a rejected cookie value is reported once per page load
// instead of on every useApi() call / every accessor that resolves the
// environment.
const warnedEnvironmentKeys = new Set<string>();

export default function useApi(event, cookieString?: string) {

  const config = useRuntimeConfig();
  const string = event ? getCookie(event, 'desired_environment') : undefined;

  const getHost = (): string => {
    const _url = event ? getRequestURL(event) : useRequestURL(event);
    return _url?.host ?? '';
  };

  // Hosted (EKS) deployments. On these URLs the "Local Docker" option is not
  // offered in the environment dropdown (it only makes sense for local dev).
  const hostedHosts = ['starryspins.ft-crm.com', 'starryspins-dev.ft-crm.com'];

  // Which backend a deployment talks to is derived from the request host, which
  // means it only works where a browser Host header exists. This relies on
  // `ssr: false` in nuxt.config.ts: under SSR the internal fetch has no Host,
  // getHost() returns the pod's own address, and production silently resolved
  // to `demo-staging`. If SSR is ever re-enabled, this needs replacing with a
  // host-independent signal (e.g. a NUXT_PUBLIC_* default environment).
  const isStarryProd = () => {
    const host = getHost().toLowerCase();
    if (!host || /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?::\d+)?$/.test(host)) return false;
    return !host.includes('dev');
  };


  const environments: Environments = {
    "e2e": {
      niceName: "Local Docker",
      apiBaseUrl: (config.apiGatewayUrl as string) || (config.public?.apiGatewayUrl as string) || "http://api-gateway",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
    "demo-production": {
      niceName: "Demo Production (Recommended)",
      apiBaseUrl: "https://demo-production.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Production',
    },
    // "dev": {
    //   niceName: "Development",
    //   apiBaseUrl: "https://dev.ft-crm.com",
    //   loaderBranch: 'loader-legacy',
    // },
    "training": {
      niceName: "Training",
      apiBaseUrl: "https://training.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
    "demo-staging": {
      niceName: "Demo Staging",
      apiBaseUrl: "https://demo-admin.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
    // "patrik-staging": {
    //   niceName: "Patrik Staging",
    //   apiBaseUrl: "https://patrik-staging.ft-crm.com",
    //   loaderBranch: 'loader',
    // },
    // "dev-staging": {
    //   niceName: "Dev Staging",
    //   apiBaseUrl: "https://dev.ft-crm.com",
    //   loaderBranch: 'loader',
    // },
    "qa-staging": {
      niceName: "QA Staging",
      apiBaseUrl: "https://qa-staging.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
    "testing-staging": {
      niceName: "Testing",
      apiBaseUrl: "https://testing.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
    "stephen-staging": {
      niceName: "Stephen Staging",
      apiBaseUrl: "https://stephen-staging.ft-crm.com",
      loaderBranch: 'loader',
      envType : 'Staging',
    },
  };

  const apiBaseUrl = () => {
    const env = getCurrentEnvironment();
    return env.apiBaseUrl;
    // return config.public.apiBaseUrl || NUXT_PUBLIC_API_BASE_URL;
  }

  const fasttrackBrandName = () => {
    let env = getCurrentEnvironmentKey();
    env = env.replace('-staging', '');
    env = env.replace('-production', '');
    return env;
    // return config.public.fasttrackBrandName || NUXT_PUBLIC_FASTTRACK_BRAND_NAME;
  };

  // Environments offered in the UI dropdown. Demo Production is intentionally
  // never listed — it stays the pre-selected environment on the prod URL via
  // getCurrentEnvironmentKey(), but is not user-selectable. Local Docker (e2e)
  // is only offered when NOT on a hosted (EKS) URL.
  const getSelectableEnvironments = (): Environments => {
    const hideLocalDocker = hostedHosts.includes(getHost());
    const selectable: Environments = {};
    for (const [key, env] of Object.entries(environments)) {
      if (key === 'demo-production') continue;
      if (key === 'e2e' && hideLocalDocker) continue;
      selectable[key] = env;
    }
    return selectable;
  };

  const cookieName = "desired_environment";

  // Own-property check: a plain `environments[key]` lookup also matches
  // inherited Object members, so a cookie of "constructor" or "toString" would
  // pass as a known environment and then blow up on `.apiBaseUrl`.
  const isKnownEnvironmentKey = (environmentKey: string): boolean => {
    return Object.prototype.hasOwnProperty.call(environments, environmentKey);
  };

  const knownEnvironmentKeys = (): string => Object.keys(environments).join(', ');

  const setDesiredEnvironment = (environmentKey: string): void => {
    if (!isKnownEnvironmentKey(environmentKey)) {
      console.error(`Environment ${environmentKey} does not exist`);
      return;
    }

    cookies.set(cookieName, environmentKey);
  }

  const getCurrentEnvType = (): 'Production' | 'Staging' => {
    return getCurrentEnvironment().envType;
  }

  const defaultEnvironmentKey = (): string => {
    return (config.apiGatewayUrl || config.public?.apiGatewayUrl) ? 'e2e' : 'demo-staging';
  }

  // The cookie is user-writable (devtools), so it is not trusted: anything that
  // is not a live key of `environments` is ignored in favour of the default.
  const requestedEnvironmentKey = (): string | undefined => {
    const requested = string || cookieString;
    if (!requested) {
      return undefined;
    }
    if (isKnownEnvironmentKey(requested)) {
      return requested;
    }

    if (!warnedEnvironmentKeys.has(requested)) {
      warnedEnvironmentKeys.add(requested);
      console.warn(
        `Ignoring ${cookieName} cookie "${requested}": not a known backend environment. ` +
        `Falling back to "${defaultEnvironmentKey()}". Known environments: ${knownEnvironmentKeys()}`
      );
    }
    return undefined;
  }

  const getCurrentEnvironmentKey = (): string => {
    if(isStarryProd()) {
      return 'demo-production'
    } ;
    return requestedEnvironmentKey() || defaultEnvironmentKey();
  }

  const getCurrentEnvironment = (): EnvironmentConfig => {
    const key = getCurrentEnvironmentKey();
    if (isKnownEnvironmentKey(key)) {
      return environments[key];
    }

    const fallbackKey = defaultEnvironmentKey();
    if (isKnownEnvironmentKey(fallbackKey)) {
      return environments[fallbackKey];
    }

    throw new Error(
      `No backend environment is configured for "${key}", and the default "${fallbackKey}" is missing too. ` +
      `Known environments: ${knownEnvironmentKeys()}`
    );
  }

  const loaderBranch = () => {
    const env = getCurrentEnvironment();
    return env.loaderBranch;
    // return config.public.loaderBranch || NUXT_PUBLIC_LOADER_BRANCH;
  };

  const enableJackpots = (config.public.enableJackpots ?? NUXT_PUBLIC_ENABLE_JACKPOTS).toString() === 'true';
  const enableLoginChallenge = (config.public.enableLoginChallenge ?? NUXT_PUBLIC_ENABLE_LOGINCHALLENGE).toString() === 'true'
  const enableChristmasPromo2026 = (config.public.enableChristmasPromo2026 ?? '').toString() === 'true';

  return {
    apiBaseUrl,
    fasttrackBrandName,
    loaderBranch,
    enableJackpots,
    enableLoginChallenge,
    enableChristmasPromo2026,
    getSelectableEnvironments,
    setDesiredEnvironment,
    getCurrentEnvironmentKey,
    getCurrentEnvironment,
    isStarryProd,
    getCurrentEnvType,
  };
}

interface EnvironmentConfig {
  niceName: string;
  apiBaseUrl: string;
  loaderBranch: string;
  envType: 'Production' | 'Staging';
}

type Environments = Record<string, EnvironmentConfig>;
